'use client';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/cn';
import { InfiniteScrollList } from '@kit/ui/infinite-scroll-list';
import { Trans } from '@kit/ui/trans';

import {
  ListingFilterBar,
  ListingFilterSheet,
  useListingFilters,
} from '~/components/listing-filters';
import { toFilterConfig } from '~/lib/listings';
import { organizationsListing } from '~/lib/listings/registries/organizations.registry';
import type { OrganizationListItem } from '../_lib/server/get-organizations';
import { loadMoreOrganizationsAction } from '../_lib/server/load-more-action';
import { OrganizationCard } from './organization-card';

type LoadMoreFn = (params: {
  page: number;
  type?: string;
  reach?: string;
  search?: string;
  sort?: string;
}) => Promise<{ organizations: OrganizationListItem[]; hasMore: boolean }>; // i18n-ignore TypeScript generic, not user-facing

// First N cards above the fold get priority loading — the rest are lazy.
const PRIORITY_COUNT = 6;

interface OrganizationsContainerProps {
  organizations: OrganizationListItem[];
  initialHasMore: boolean;
  /**
   * When set, hides the type filter and keeps this type locked.
   * Used by category browsing pages (e.g. /home/community/organizations/category/school).
   */
  lockedType?: string;
  /**
   * Public surfaces ((adaptive)/organizations) pass the no-auth
   * loadMoreOrganizationsPublicAction; the authed community pages use the
   * default. See load-more-public-action.ts (BALLEE-D7 / BAL-307).
   */
  loadMoreAction?: LoadMoreFn;
}

export function OrganizationsContainer({
  organizations,
  initialHasMore,
  lockedType,
  loadMoreAction = loadMoreOrganizationsAction,
}: OrganizationsContainerProps) {
  const { t } = useTranslation();

  // toFilterConfig wants a simple (key, default?) => string; wrap react-i18next's
  // TFunction so its overload set doesn't clash with that narrower signature.
  const translate = useCallback(
    (key: string, defaultValue?: string) =>
      defaultValue ? t(key, { defaultValue }) : t(key),
    [t],
  );

  // Build the filter config from the org registry (BAL-664). On category pages
  // the type is locked, so drop the `type` facet from the bar.
  const config = useMemo(() => {
    const base = toFilterConfig(organizationsListing, translate, {
      searchPlaceholderKey: 'community:organizations.searchPlaceholder',
    });
    if (lockedType) {
      return { ...base, facets: base.facets.filter((f) => f.key !== 'type') };
    }
    return base;
  }, [translate, lockedType]);

  const filters = useListingFilters(config);
  const { state, isPending } = filters;

  // The active filter values, threaded to the load-more action.
  const activeType = lockedType ?? state.facets.type?.[0];
  const activeReach = (state.facets.reach ?? []).join(',');

  const isEmpty = organizations.length === 0 && !isPending;

  // Remount the list when the APPLIED filters change so paging state resets to
  // the new SSR page-0.
  const listKey = `${activeType ?? ''}|${activeReach}|${state.search}|${state.sort}`;

  return (
    <div className="space-y-6">
      {/* Web filter bar (≥sm) + mobile filter sheet (<sm). */}
      <div className="hidden px-4 sm:block">
        <ListingFilterBar config={config} filters={filters} />
      </div>
      <div className="px-4 sm:hidden">
        <ListingFilterSheet config={config} filters={filters} />
      </div>

      {isEmpty ? (
        <div className="flex min-h-[40vh] items-center justify-center px-4">
          <div className="max-w-sm space-y-2 text-center">
            <p className="text-foreground text-base font-medium">
              <Trans
                i18nKey="organizations:empty.title"
                defaults="No organizations found"
              />
            </p>
            <p className="text-muted-foreground text-sm">
              <Trans
                i18nKey="organizations:empty.subtitle"
                defaults="Try adjusting your filters"
              />
            </p>
          </div>
        </div>
      ) : (
        <InfiniteScrollList<OrganizationListItem>
          key={listKey}
          initialItems={organizations}
          initialItemKeys={organizations.map((org) => org.id)}
          initialHasMore={initialHasMore}
          loadMore={async (page) => {
            const result = await loadMoreAction({
              page,
              type: activeType,
              reach: activeReach,
              search: state.search,
              sort: state.sort,
            });
            return { items: result.organizations, hasMore: result.hasMore };
          }}
          renderItem={(org, index) => (
            <OrganizationCard
              key={org.id}
              organization={org}
              priority={index < PRIORITY_COUNT}
            />
          )}
          getItemKey={(org) => org.id}
          gridClassName={cn(
            'grid gap-x-5 gap-y-8 px-4 transition-opacity duration-200 sm:gap-x-6 sm:gap-y-10',
            isPending && 'opacity-60',
          )}
          gridStyle={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          }}
          trigger="auto"
          ariaLabel={t('community:organizations.listAriaLabel')}
          loadMoreLabel={
            <Trans
              i18nKey="organizations:loadMore"
              defaults="Load more organizations"
            />
          }
        />
      )}
    </div>
  );
}
